import { useQuery } from '@tanstack/react-query';
import { Cpu } from 'lucide-react';

import { Badge, Card, CardContent, CardHeader } from '@/components/ui';
import { adminApi } from '@/lib/api';

/**
 * Read-only LLM governance view (Spec S09 §"LLM model governance").
 *
 * MVP shows the active providers + their capabilities. Per-skill model
 * assignment editing lives in Studio (Skill Editor) — this is the
 * organisation-level inventory.
 */
export function LLMGovernance() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llm-providers'],
    queryFn: () => adminApi.llmProviders(),
  });

  return (
    <div className="mx-auto max-w-4xl px-6 py-6">
      <h1 className="text-display text-brand-900">LLM governance</h1>
      <p className="mt-1 text-body text-slate-600">
        Active model providers used across the pipeline. Change skill-level
        assignments in Studio.
      </p>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-accent" aria-hidden />
            <h2 className="text-h2 text-brand-900">Providers</h2>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="px-4 py-3 text-small text-slate-500">Loading…</p>}
          {error && (
            <p className="px-4 py-3 text-small text-red-700">Failed to load providers.</p>
          )}
          {data && (
            <table className="w-full text-body">
              <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left">Model</th>
                  <th className="px-4 py-2 text-left">Display</th>
                  <th className="px-4 py-2 text-left">Capabilities</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map((p) => (
                  <tr key={p.provider_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-small text-slate-700">{p.model_id}</td>
                    <td className="px-4 py-2 text-brand-900">{p.display_name}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {p.capabilities.map((cap) => (
                          <Badge key={cap} variant="neutral">
                            {cap}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
